import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Paper, Snackbar, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import useTabTitle from "../hooks/useTabTitle.hook";
import {
  getTermsAndConditions,
  updateTermsAndConditions,
} from "../redux/slices/termsAndConditionsSlice";
import { TermsAndConditionsItem } from "../interfaces/terms-and-conditions-interface";
import StarterKit from "@tiptap/starter-kit";
import {
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  RichTextEditor,
} from "mui-tiptap";
import Placeholder from "@tiptap/extension-placeholder";
import DeleteIcon from "@mui/icons-material/Delete";
import useDetectSession from "../hooks/useDetectSession.hook";
import {
  pageHeading,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import AppContainer from "../components/AppContainer";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";
import parse from "html-react-parser";
import { List, arrayMove } from "react-movable";

const TermsAndConditions: React.FC = () => {
  useTabTitle();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [updatedTAndCs, setUpdatedTAndCs] = useState<TermsAndConditionsItem[]>(
    []
  );

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const { termsAndConditions } = useSelector(
    (state: RootState) => state.termsAndConditions
  );
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);

  const [currentUserPermissions, setCurrentUserPermissions] =
    useState<PermissionsByDocKey>();

  const [edit, setEdit] = useState<boolean>(false);
  const [rearrangeSections, setRearrangeSections] = useState<boolean>(false);

  const idToken = localStorage.getItem("idToken");
  useDetectSession();

  StarterKit.configure({
    blockquote: false,
    code: false,
    codeBlock: false,
    dropcursor: false,
    gapcursor: false,
    hardBreak: false,
    history: false,
    horizontalRule: false,
    strike: false,
  });

  const handleSaveClick = () => {
    dispatch(
      updateTermsAndConditions({
        updatedTerms: updatedTAndCs,
        updatedBy: currentUser?.fullname ?? "",
      })
    )
      .unwrap()
      .then(() => {
        setSnackbarMessage("Terms and conditions updated successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
        dispatch(getTermsAndConditions());
      })
      .catch((err: any) => {
        setSnackbarMessage(`Failed to update terms and conditions: ${err}`);
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      });
  };

  useEffect(() => {
    if (!idToken) {
      navigate("/login");
      return;
    }

    dispatch(getTermsAndConditions());
  }, [dispatch, idToken, navigate]);

  useEffect(() => {
    setUpdatedTAndCs(termsAndConditions.terms);
  }, [termsAndConditions]);

  useEffect(() => {
    if (currentUser && currentUser.userPermissions) {
      setCurrentUserPermissions(transformArrToObj(currentUser.userPermissions));
    }
  }, [currentUser]);

  return (
    <AppContainer>
      <Box sx={{ flexGrow: 1, padding: 2 }}>
        <Box display="flex">
          <Typography variant="h4" sx={pageHeading}>
            Terms and Conditions
          </Typography>
          {currentUserPermissions?.termsAndConditions?.permissions?.edit && (
            <Button
              style={{ position: "absolute", right: 15 }}
              variant="contained"
              onClick={() => {
                setEdit(true);
              }}
              sx={primaryBtnStyle}
              disabled={edit}
            >
              Edit
            </Button>
          )}
        </Box>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
        >
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        {!edit && (
          <Paper
            elevation={4}
            sx={{
              padding: 3,
              mt: 3,
            }}
          >
            {termsAndConditions?.terms?.map((item) => (
              <Box
                display="flex"
                key={item.title}
                flexDirection={"column"}
                margin={5}
              >
                <Typography variant="h5">{item.title}</Typography>
                {parse(item.content)}
              </Box>
            ))}
          </Paper>
        )}
        {edit && termsAndConditions.terms && (
          <List
            values={updatedTAndCs}
            onChange={({ oldIndex, newIndex }) => {
              setUpdatedTAndCs(arrayMove(updatedTAndCs, oldIndex, newIndex));
            }}
            disabled={!rearrangeSections}
            renderList={({ children, props }) => (
              <ul
                style={{ listStyle: "none", margin: "0px", padding: "0px" }}
                {...props}
              >
                {children}
              </ul>
            )}
            renderItem={({ value, index, props, isDragged }) => (
              <li {...props}>
                <Box
                  display="flex"
                  key={value.title}
                  flexDirection={"column"}
                  margin={5}
                  sx={{
                    border: isDragged ? "2px" : undefined,
                    borderColor: isDragged ? "red" : undefined,
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    color="error"
                    style={{
                      position: "relative",
                      top: 42,
                      right: 10,
                      zIndex: 10,
                      width: "140px",
                      alignSelf: "flex-end",
                    }}
                    onClick={() => {
                      if (index) {
                        setUpdatedTAndCs(
                          updatedTAndCs.filter(
                            (tAndC, tAndCIndex) => tAndCIndex !== index
                          )
                        );
                      }
                    }}
                  >
                    Delete
                  </Button>
                  <RichTextEditor
                    content={value.title}
                    extensions={[
                      StarterKit,
                      Placeholder.configure({
                        placeholder: "Type Section Title here...",
                      }),
                    ]}
                    renderControls={() => (
                      <MenuControlsContainer>
                        <MenuSelectHeading />
                        <MenuDivider />
                        <MenuButtonBold />
                        <MenuButtonItalic />
                        <MenuButtonOrderedList />
                      </MenuControlsContainer>
                    )}
                    onUpdate={({ editor }) => {
                      const newVal = editor.getHTML();
                      setUpdatedTAndCs(
                        updatedTAndCs?.map((newItem, currentIndex) => {
                          if (currentIndex === index) {
                            return { title: newVal, content: newItem.content };
                          }
                          return newItem;
                        })
                      );
                    }}
                    editable={
                      currentUserPermissions?.termsAndConditions?.permissions
                        ?.edit === true
                    }
                  />
                  <RichTextEditor
                    content={value.content}
                    extensions={[
                      StarterKit,
                      Placeholder.configure({
                        placeholder: "Type Section Content here...",
                      }),
                    ]}
                    renderControls={() => (
                      <MenuControlsContainer>
                        <MenuSelectHeading />
                        <MenuDivider />
                        <MenuButtonBold />
                        <MenuButtonItalic />
                        <MenuButtonOrderedList />
                      </MenuControlsContainer>
                    )}
                    onUpdate={({ editor }) => {
                      const newVal = editor.getHTML();
                      setUpdatedTAndCs(
                        updatedTAndCs?.map((newItem, currentIndex) => {
                          if (currentIndex === index) {
                            return { title: newItem.title, content: newVal };
                          }
                          return newItem;
                        })
                      );
                    }}
                    editable={
                      currentUserPermissions?.termsAndConditions?.permissions
                        ?.edit === true
                    }
                  />
                </Box>
              </li>
            )}
          />
        )}
        {edit &&
          currentUserPermissions?.termsAndConditions?.permissions?.edit && (
            <Box
              display="flex"
              margin={2}
              flexDirection={"row"}
              alignItems={"flex-end"}
              justifyContent={"flex-end"}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setUpdatedTAndCs([
                    ...updatedTAndCs,
                    {
                      title: "",
                      content: "",
                    },
                  ]);
                }}
                style={{ position: "absolute", left: 350 }}
                sx={primaryBtnStyle}
              >
                Add New Section
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setRearrangeSections(!rearrangeSections);
                }}
                style={{ position: "absolute", left: 550 }}
                sx={primaryBtnStyle}
              >
                {rearrangeSections ? "Continue Editing" : "Rearrange Sections"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setEdit(false);
                }}
                sx={primaryBtnStyleOutline}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 15 }}
                onClick={() => handleSaveClick()}
                sx={primaryBtnStyle}
              >
                Update Terms and Conditions
              </Button>
            </Box>
          )}
      </Box>
    </AppContainer>
  );
};

export default TermsAndConditions;
